import React, { useEffect, useState, useCallback } from "react";
import {
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Chip,
    Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
    createPusherInstance,
    subscribeToQueueUpdates,
} from "../../../config/pusher";
import { getQueues, updateQueueStatus, addQueue, addDirectAssistQueue, getServiceSettings } from "./../../../api/api";
import { getCurrentBranchId, getCurrentUser, debugAuthData } from "../../../utils/auth";
import Logo from "../../../assests/logo.png";

// --------------------------------------------------
// Queue Card
// --------------------------------------------------
const QueueCard = ({ queue, onAssist, onDone, onLost, serviceSelectionEnabled }) => {
    const [currentTime, setCurrentTime] = useState(new Date());

    // Debug logging
    // console.log("QueueCard: Rendering queue:", queue);

    // Update current time every 10 seconds to refresh wait time display
    useEffect(() => {
        // Set initial time immediately
        setCurrentTime(new Date());

        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 10000); // Update every 10 seconds for responsive display

        return () => clearInterval(timer);
    }, []);

    const calculateWaitTime = (regTime, svcTime, status) => {
        // Handle invalid or future registration times
        if (!regTime) {
            return "0m";
        }

        // If status is SERVING and we have QueueSvcTime, use that instead of current time
        // This stops the wait time from updating after Assist is pressed
        let endTime;
        if (status === 'SERVING' && svcTime) {
            // Use QueueSvcTime - wait time is frozen at assist time
            endTime = new Date(svcTime);
        } else {
            // Use current time for WAITING queues
            const now = new Date();
            endTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Oslo"}));
        }

        // Parse registration time (stored in Norway timezone format)
        const regDate = new Date(regTime);
        
        // Check if the parsed time is valid
        if (isNaN(regDate.getTime())) {
            return "0m";
        }

        // If the registration time is in the future, return "0m"
        if (regDate > endTime) {
            return "0m";
        }

        // Calculate time difference in minutes
        const diff = Math.floor((endTime - regDate) / (1000 * 60)); // difference in minutes
        console.log("🔍 Time Difference (minutes):", diff);

        // If the difference is negative or zero, return "0m"
        if (diff <= 0) {
            return "0m";
        }

        // For very recent registrations (less than 2 minutes), show "0m" to indicate just started
        if (diff < 2) {
            return "0m";
        }

        // If the registration time is more than 24 hours ago, it's likely an old queue
        // or there's a timezone issue - treat it as a new queue
        const hoursDiff = diff / 60;
        if (hoursDiff > 24) {
            return "0m";
        }

        // If the registration time is not from today, treat it as a new queue
        const regDateStr = regDate.toLocaleDateString('no-NO', { timeZone: 'Europe/Oslo' });
        const endDateStr = endTime.toLocaleDateString('no-NO', { timeZone: 'Europe/Oslo' });
        if (regDateStr !== endDateStr) {
            return "0m";
        }

        const hours = Math.floor(diff / 60);
        const minutes = diff % 60;
        const result = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        console.log("🔍 Calculated Wait Time:", result);
        return result;
    };

    return (
        <Card
            sx={{
                textAlign: "center",
                p: 0.75,
                boxShadow: 3,
                backgroundColor:
                    queue.status === "SERVING"
                        ? "#fff9c4"
                        : queue.status === "WAITING"
                        ? "#f9f9f9"
                        : "#e8f5e8",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
            }}
        >
            <CardContent sx={{ p: "0 !important", flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.375}
                >
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", flexGrow: 1, fontSize: { xs: "2.25rem", sm: "2.75rem" } }}
                    >
                     {queue.number}
                    </Typography>

                    {queue.status === "SERVING" ? (
                        <Box sx={{ display: "flex", gap: 0.375, ml: 1, flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.5, px: 1, minWidth: "auto", lineHeight: 1.2 }}
                                onClick={() => onDone(queue.id)}
                            >
                                Done
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    backgroundColor: "red",
                                    color: "white",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    py: 0.5,
                                    px: 1,
                                    minWidth: "auto",
                                    lineHeight: 1.2,
                                }}
                                onClick={() => onLost(queue.id)}
                            >
                                Lost
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="contained"
                            size="small"
                            sx={{
                                backgroundColor: "green",
                                color: "white",
                                textTransform: "none",
                                ml: 1,
                                fontSize: "0.75rem",
                                py: 0.5,
                                px: 1,
                                minWidth: "auto",
                                lineHeight: 1.2,
                            }}
                            onClick={() => onAssist(queue.id)}
                        >
                            Assist
                        </Button>
                    )}
                </Box>

                <Box textAlign="left" mt={0.75}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Wait time updates every 10 seconds">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                            ⏱ Wait Time: {calculateWaitTime(queue.QueueRegTime, queue.QueueSvcTime, queue.status)}
                        </Typography>
                    </Tooltip>
                    </Box>
                    <style>
                        {`
                            @keyframes pulse {
                                0% { opacity: 1; }
                                50% { opacity: 0.7; }
                                100% { opacity: 1; }
                            }
                        `}
                    </style>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.125, fontSize: "0.75rem", lineHeight: 1.2 }}>
                            Services:
                        </Typography>
                        {queue.description &&
                        queue.description !== "Ticket generated via API" &&
                        queue.description !== "Ticket generated by staff" &&
                        queue.description !== "No services selected" &&
                        queue.description !== "Queue created - services pending" ? (
                            <Box component="ul" sx={{ pl: 1.5, m: 0, mb: 0, mt: 0.125 }}>
                                {queue.description
                                    .split(/[,;]\s*|\n/)
                                    .map((service, index) => {
                                        const trimmedService = service.trim();
                                        return trimmedService ? (
                                            <Typography
                                                key={index}
                                                component="li"
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 0.0625, fontSize: "0.75rem", lineHeight: 1.2 }}
                                            >
                                                {trimmedService}
                                            </Typography>
                                        ) : null;
                                    })
                                    .filter(Boolean)}
                            </Box>
                        ) : queue.description === "Queue created - services pending" && serviceSelectionEnabled ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                                Service Pending
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                                Not selected
                            </Typography>
                        )}
                    </Box>

                    {/* CRM Profile Link */}
                    {queue.crm_profile_url && (
                        <Box mt={0.375}>
                            <Button
                                variant="outlined"
                                size="small"
                                href={queue.crm_profile_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.7rem",
                                    py: 0.25,
                                    px: 0.75,
                                    minWidth: "auto",
                                    lineHeight: 1.2,
                                }}
                            >
                                🔗 CRM Profile
                            </Button>
                        </Box>
                    )}

                    {/* Customer Info */}
                    {queue.customer_name && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mt={0.375}
                            sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
                        >
                            👤 {queue.customer_name}
                        </Typography>
                    )}

                    {queue.phone_number && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                            📞 {queue.phone_number}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// --------------------------------------------------
// Add Queue Card
// --------------------------------------------------
const AddQueueCard = ({ onAdd, onDirectAssist, adding, addingDirectAssist }) => (
    <Card
        sx={{
            textAlign: "center",
            p: 0.75,
            boxShadow: 3,
            backgroundColor: "#e3f2fd",
            border: "2px dashed #2196f3",
            cursor: "pointer",
            transition: "0.3s",
            "&:hover": { backgroundColor: "#bbdefb" },
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
        }}
    >
        <CardContent sx={{ p: "0 !important" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.75, fontSize: "0.9rem" }}>
                ADD QUEUE
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onAdd}
                    disabled={adding || addingDirectAssist}
                    size="small"
                    sx={{ mb: 0.5 }}
                >
                    {adding ? (
                        <CircularProgress size={20} sx={{ color: "#2196f3" }} />
                    ) : (
                        "Add"
                    )}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={onDirectAssist}
                    disabled={adding || addingDirectAssist}
                    size="small"
                    sx={{ 
                        backgroundColor: "#4caf50",
                        "&:hover": { backgroundColor: "#45a049" }
                    }}
                >
                    {addingDirectAssist ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                        "Direct Assist"
                    )}
                </Button>
            </Box>
        </CardContent>
    </Card>
);

// --------------------------------------------------
// Empty State Card
// --------------------------------------------------
// --------------------------------------------------
// Empty State Card
// --------------------------------------------------
const EmptyState = ({ onAdd, onDirectAssist, adding, addingDirectAssist }) => (
    <Card
        sx={{
            textAlign: "center",
            p: 3,
            boxShadow: 3,
            backgroundColor: "#f9f9f9",
        }}
    >
        <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
                NO CUSTOMERS WAITING
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onAdd}
                    disabled={adding || addingDirectAssist}
                >
                    {adding ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                        "Add Queue"
                    )}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={onDirectAssist}
                    disabled={adding || addingDirectAssist}
                    sx={{ 
                        backgroundColor: "#4caf50",
                        "&:hover": { backgroundColor: "#45a049" }
                    }}
                >
                    {addingDirectAssist ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                        "Direct Assist"
                    )}
                </Button>
            </Box>
        </CardContent>
    </Card>
);

// --------------------------------------------------
// Queue List Wrapper
// --------------------------------------------------
const QueueList = ({ queues, onAssist, onDone, onLost, onAdd, onDirectAssist, adding, addingDirectAssist, serviceSelectionEnabled }) => {
    if (!queues.length) {
        return (
            <Grid item xs={12}>
                <EmptyState 
                    onAdd={onAdd} 
                    onDirectAssist={onDirectAssist}
                    adding={adding} 
                    addingDirectAssist={addingDirectAssist}
                />
            </Grid>
        );
    }

    return (
        <>
            {queues.map((queue) => (
                <Grid item xs={6} sm={2} md={2} key={queue.id} sx={{ mb: 1 }}>
                    <QueueCard
                        queue={queue}
                        onAssist={onAssist}
                        onDone={onDone}
                        onLost={onLost}
                        serviceSelectionEnabled={serviceSelectionEnabled}
                    />
                </Grid>
            ))}
            <Grid item xs={12} sm={2} md={2} sx={{ mb: 1 }}>
                <AddQueueCard 
                    onAdd={onAdd} 
                    onDirectAssist={onDirectAssist}
                    adding={adding} 
                    addingDirectAssist={addingDirectAssist}
                />
            </Grid>
        </>
    );
};

// --------------------------------------------------
// Main Staff Component
// --------------------------------------------------
const Staff = () => {
    const navigate = useNavigate();
    const [queues, setQueues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingQueue, setAddingQueue] = useState(false);
    const [addingDirectAssist, setAddingDirectAssist] = useState(false);
    const [newQueueId, setNewQueueId] = useState(null);
    const [serviceSelectionEnabled, setServiceSelectionEnabled] = useState(true);

    const branchId = getCurrentBranchId();
    const currentUser = getCurrentUser();
    const branchName = currentUser?.branch_name || "Unknown Branch";
    
    // Call debug function to show all auth data
    debugAuthData();

    const handleAddQueue = async () => {
        try {
            setAddingQueue(true); // Set loading state immediately
            
            // Get current time in Norway timezone (Europe/Oslo) to match backend storage
            const now = new Date();
            const norwayTime = new Intl.DateTimeFormat('sv-SE', {
                timeZone: 'Europe/Oslo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }).format(now).replace('T', ' ').replace('Z', '');
            
            console.log("PC Time Details:", {
                UTC: now.toISOString(),
                Norway: norwayTime,
                Local: now.toLocaleString(),
                Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            
            const newQueue = await addQueue(branchId, norwayTime);
            console.log("✅ Queue created successfully:", newQueue);
            
            if (newQueue) {
                setNewQueueId(newQueue.id);
                // Reset loading state after successful creation
                setTimeout(() => {
                    setAddingQueue(false);
                    setNewQueueId(null);
                }, 1000); // Give time for the queue to appear in the list
            } else {
                setAddingQueue(false);
            }
        } catch (error) {
            console.error("❌ Error adding queue:", error);
            setAddingQueue(false); // Reset loading state on error
        }
    };

    const handleAddDirectAssist = async () => {
        try {
            setAddingDirectAssist(true); // Set loading state immediately
            
            // Get current time in Norway timezone (Europe/Oslo) to match backend storage
            const now = new Date();
            const norwayTime = new Intl.DateTimeFormat('sv-SE', {
                timeZone: 'Europe/Oslo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }).format(now).replace('T', ' ').replace('Z', '');
            
            console.log("Direct Assist - PC Time Details:", {
                UTC: now.toISOString(),
                Norway: norwayTime,
                Local: now.toLocaleString(),
                Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            
            const newQueue = await addDirectAssistQueue(branchId, norwayTime);
            console.log("✅ Direct assist queue created successfully:", newQueue);
            
            if (newQueue) {
                setNewQueueId(newQueue.id);
                // Reset loading state after successful creation
                setTimeout(() => {
                    setAddingDirectAssist(false);
                    setNewQueueId(null);
                }, 1000); // Give time for the queue to appear in the list
            } else {
                setAddingDirectAssist(false);
            }
        } catch (error) {
            console.error("❌ Error adding direct assist queue:", error);
            setAddingDirectAssist(false); // Reset loading state on error
        }
    };

    const fetchQueues = useCallback(async () => {
        try {
            console.log("🔍 Fetching queues for branchId:", branchId);
            const data = await getQueues(branchId);
            console.log("📊 Received queue data:", data);
            console.log("📊 Queue count:", data?.length || 0);
            setQueues(data);
        } catch (error) {
            console.error("❌ Error fetching queues:", error);
        } finally {
            setLoading(false);
        }
    }, [branchId]);

    const handleAssist = useCallback(
        async (id) => {
            try {
                await updateQueueStatus(id, branchId, "SERVING");
                setQueues((prev) =>
                    prev.map((q) =>
                        q.id === id
                            ? { ...q, status: "SERVING", isAssisting: true }
                            : q
                    )
                );
            } catch (error) {
                console.error("❌ Staff: Error marking assist:", error);
            }
        },
        [branchId]
    );

    const handleDone = useCallback(
        async (id) => {
            try {
                await updateQueueStatus(id, branchId, "ASSISTED");
                setQueues((prev) => prev.filter((q) => q.id !== id));
            } catch (error) {
                console.error("❌ Staff: Error finishing assist:", error);
            }
        },
        [branchId]
    );

    const handleLost = useCallback(
        async (id) => {
            try {
                await updateQueueStatus(id, branchId, "LOST");
                setQueues((prev) => prev.filter((q) => q.id !== id));
            } catch (error) {
                console.error("❌ Staff: Error marking queue as lost:", error);
            }
        },
        [branchId]
    );

    // Fetch service selection status
    useEffect(() => {
        const fetchServiceSettings = async () => {
            try {
                const settings = await getServiceSettings(branchId);
                if (settings.success) {
                    setServiceSelectionEnabled(settings.data?.service_selection_enabled ?? true);
                }
            } catch (error) {
                console.error("Error fetching service settings:", error);
                // Default to true if there's an error
                setServiceSelectionEnabled(true);
            }
        };
        fetchServiceSettings();
    }, [branchId]);

    useEffect(() => {
        fetchQueues();
        const pusher = createPusherInstance();
        const channel = subscribeToQueueUpdates(pusher, (data) => {
            console.log("🔄 Queue update received:", data);
            
            // Handle different types of queue updates
            const queueData = data.queue || data.data?.queue || data;
            const action = data.action || data.data?.action;
            
            if (queueData) {
                // Get queue ID and branch ID
                const queueId = queueData.QueueID || queueData.id;
                const queueBranchId = queueData.QueueBranchID || queueData.branch_id || queueData.QueueBranchID;
                const queueStatus = queueData.QueueStatus || queueData.status;
                
                console.log("📊 Queue data:", {
                    id: queueId,
                    status: queueStatus,
                    action: action,
                    branchId: queueBranchId,
                    currentBranchId: branchId
                });
                
                // Check if this update is for our branch (if branch_id is provided)
                if (queueBranchId && branchId && queueBranchId.toString() !== branchId.toString()) {
                    console.log("⏭️ Skipping update - different branch:", {
                        queueBranchId,
                        currentBranchId: branchId
                    });
                    return; // Ignore updates from other branches
                }
                
                // If queue is auto-expired or marked as LOST/ASSISTED, remove it immediately
                if (action === 'auto_expired' || 
                    action === 'auto_expire' ||
                    queueStatus === 'LOST' || 
                    queueStatus === 'ASSISTED') {
                    
                    console.log("🗑️ Removing queue from staff panel:", {
                        queueId: queueId,
                        queueNumber: queueData.QueueNr || queueData.number,
                        status: queueStatus,
                        action: action
                    });
                    
                    // Show notification for auto-expired queues
                    if (action === 'auto_expired' || action === 'auto_expire') {
                        console.log("⏰ Queue auto-expired after 5 minutes of service:", {
                            queueNumber: queueData.QueueNr || queueData.number,
                            queueId: queueId,
                            branchId: queueBranchId
                        });
                    }
                    
                    // Remove the queue from the list
                    setQueues((prev) => {
                        const filtered = prev.filter((q) => {
                            const qId = q.id || q.QueueID;
                            return qId !== queueId;
                        });
                        console.log("📋 Queue list updated:", {
                            before: prev.length,
                            after: filtered.length,
                            removed: prev.length - filtered.length
                        });
                        return filtered;
                    });
                } else {
                    // For other updates (SERVING, WAITING, etc.), refresh the entire queue list
                    console.log("🔄 Refreshing queue list for update:", action || queueStatus);
                    fetchQueues();
                }
            } else {
                // Fallback: refresh all queues if no specific queue data
                console.log("🔄 No queue data in update, refreshing all queues");
                fetchQueues();
            }
        });

        return () => {
            if (channel) {
                channel.unbind("queue.updated");
            }
            if (pusher) {
                pusher.unsubscribe("queues");
                pusher.disconnect();
            }
        };
    }, [fetchQueues, branchId]);

    useEffect(() => {
        if (newQueueId && queues.find((q) => q.id === newQueueId)) {
            console.log("✅ New queue detected in list, resetting loading state");
            setAddingQueue(false);
            setNewQueueId(null);
        }
    }, [queues, newQueueId]);

    // Fallback: Reset loading state if it's been stuck for too long
    useEffect(() => {
        if (addingQueue) {
            const timeout = setTimeout(() => {
                console.log("⚠️ Adding queue timeout - resetting loading state");
                setAddingQueue(false);
                setNewQueueId(null);
            }, 5000); // 5 second timeout

            return () => clearTimeout(timeout);
        }
    }, [addingQueue]);

    // Fallback: Reset loading state for direct assist if it's been stuck for too long
    useEffect(() => {
        if (addingDirectAssist) {
            const timeout = setTimeout(() => {
                console.log("⚠️ Adding direct assist queue timeout - resetting loading state");
                setAddingDirectAssist(false);
                setNewQueueId(null);
            }, 5000); // 5 second timeout

            return () => clearTimeout(timeout);
        }
    }, [addingDirectAssist]);

    // Fix scrolling issue on mobile
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        // Save original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalBodyHeight = bodyElement.style.height;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalHtmlHeight = htmlElement.style.height;
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;
        
        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add CSS class
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        return () => {
            // Restore original styles
            bodyElement.style.overflow = originalBodyOverflow;
            bodyElement.style.height = originalBodyHeight;
            htmlElement.style.overflow = originalHtmlOverflow;
            htmlElement.style.height = originalHtmlHeight;
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow;
                rootElement.style.height = originalRootHeight;
            }
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 3 }}
            >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <img
                        src={Logo}
                        alt="Logo"
                        style={{
                            height: "32px",
                            marginRight: "16px",
                        }}
                    />
                    <Button variant="contained" sx={{ mb: { xs: 2, sm: 0 } }}>
                        LOCATION: {branchName.toUpperCase()}
                    </Button>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/home")}
                >
                    ADMIN
                </Button>
            </Box>

            {/* Content */}
            {loading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={1} alignItems="stretch">
                    <QueueList
                        queues={queues}
                        onAssist={handleAssist}
                        onDone={handleDone}
                        onLost={handleLost}
                        onAdd={handleAddQueue}
                        onDirectAssist={handleAddDirectAssist}
                        adding={addingQueue}
                        addingDirectAssist={addingDirectAssist}
                        serviceSelectionEnabled={serviceSelectionEnabled}
                    />
                </Grid>
            )}
        </Box>
    );
};

export default Staff;
